import React, { useMemo, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { Wand2, X, Sparkles, ArrowRight } from "lucide-react";
import { useApp } from "../AppContext";

/**
 * AI Guide — floating bottom-left button. Explains the current page in plain language
 * and links the user to the PROGÈRE AI assistant for deeper help.
 */
const GUIDES_FR = {
  "/app": {
    title: "Tableau de bord",
    body: "Vue d'ensemble de votre activité : soumissions, factures et performances. Utilisez le menu de gauche pour naviguer et le bouton + pour créer une nouvelle soumission.",
    tips: ["Créez une soumission depuis « Nouvelle soumission »", "Suivez vos revenus dans « Analytique »"],
  },
  "/app/analytics": {
    title: "Analytique",
    body: "Suivez vos taux d'acceptation, revenus et marges. Les données se rafraîchissent en temps réel.",
    tips: ["Comparez plusieurs périodes", "Exportez les rapports en CSV"],
  },
  "/app/quotes/new": {
    title: "Nouvelle soumission",
    body: "Décrivez le projet dans la boîte de description ou dictez-le au micro. L'IA préremplit vos articles avec quantités, matériaux et main-d'œuvre.",
    tips: ["Utilisez le micro pour dicter", "Choisissez un modèle pour aller plus vite"],
  },
  "/app/quotes": {
    title: "Soumissions",
    body: "Liste de toutes vos soumissions. Filtrez par statut, envoyez par courriel, exportez en PDF ou convertissez en facture.",
    tips: ["Cliquez sur une ligne pour éditer", "Partagez un lien public au client"],
  },
  "/app/invoices": {
    title: "Factures",
    body: "Gérez vos factures TPS/TVQ et suivez les paiements. Les factures peuvent être générées depuis une soumission approuvée.",
    tips: ["Exportez le rapport fiscal", "Configurez les taxes dans Paramètres"],
  },
  "/app/clients": {
    title: "Clients",
    body: "Votre carnet d'adresses. Ajoutez, éditez et liez des clients à vos projets.",
    tips: [],
  },
  "/app/templates": {
    title: "Modèles",
    body: "Gagnez du temps avec des modèles réutilisables pour vos types de travaux fréquents.",
    tips: [],
  },
  "/app/reports": {
    title: "Rapports fiscaux",
    body: "Exportez les rapports TPS/TVQ pour votre comptable.",
    tips: [],
  },
  "/app/assistant": {
    title: "Assistant PROGÈRE AI",
    body: "Posez toute question sur vos soumissions, rédigez des textes ou obtenez des estimés instantanés.",
    tips: [],
  },
  "/app/billing": {
    title: "Abonnement",
    body: "Gérez votre plan Starter/Pro/Entreprise. Paiement sécurisé par Stripe, annulable à tout moment.",
    tips: [],
  },
  "/app/settings": {
    title: "Paramètres",
    body: "Configurez votre entreprise, numéros de taxes, taux, marges, 2FA et vos droits Loi 25.",
    tips: [],
  },
  "/app/integrations": {
    title: "Intégrations",
    body: "Connectez PROGÈRE Devis AI à votre ERP central et vos outils tiers.",
    tips: [],
  },
};

const GUIDES_EN = {
  "/app": {
    title: "Dashboard",
    body: "Overview of your business — quotes, invoices and performance. Use the left menu to navigate and the + button to create a new quote.",
    tips: ["Create a quote from « New quote »", "Track revenue in « Analytics »"],
  },
  "/app/analytics": {
    title: "Analytics",
    body: "Track acceptance rate, revenue and margins in real time.",
    tips: ["Compare multiple periods", "Export reports as CSV"],
  },
  "/app/quotes/new": {
    title: "New quote",
    body: "Describe the project in the description box or dictate it. AI will prefill items with quantities, materials and labor.",
    tips: ["Use the mic to dictate", "Pick a template to go faster"],
  },
  "/app/quotes": { title: "Quotes", body: "All your quotes. Filter, email, export as PDF or convert to invoice.", tips: [] },
  "/app/invoices": { title: "Invoices", body: "Manage GST/QST invoices and payments.", tips: [] },
  "/app/clients": { title: "Clients", body: "Your address book. Add, edit and link clients to projects.", tips: [] },
  "/app/templates": { title: "Templates", body: "Save time with reusable templates for common work types.", tips: [] },
  "/app/reports": { title: "Tax reports", body: "Export GST/QST reports for your accountant.", tips: [] },
  "/app/assistant": { title: "PROGÈRE AI assistant", body: "Ask anything about your quotes or draft texts and estimates.", tips: [] },
  "/app/billing": { title: "Billing", body: "Manage Starter/Pro/Enterprise. Secure Stripe billing.", tips: [] },
  "/app/settings": { title: "Settings", body: "Company profile, tax numbers, defaults, 2FA and Bill 25 rights.", tips: [] },
  "/app/integrations": { title: "Integrations", body: "Connect PROGÈRE Devis AI to your ERP and 3rd-party tools.", tips: [] },
};

function findGuide(path, lang) {
  const dict = lang === "fr" ? GUIDES_FR : GUIDES_EN;
  // Exact match first
  if (dict[path]) return dict[path];
  // Longest prefix match
  const keys = Object.keys(dict).sort((a, b) => b.length - a.length);
  for (const k of keys) if (path.startsWith(k)) return dict[k];
  return null;
}

export default function AIGuide() {
  const { lang } = useApp();
  const nav = useNavigate();
  const loc = useLocation();
  const [open, setOpen] = useState(false);
  const isFr = lang === "fr";

  const guide = useMemo(() => findGuide(loc.pathname, lang), [loc.pathname, lang]);
  if (!guide) return null;

  return (
    <>
      {!open && (
        <button
          onClick={() => setOpen(true)}
          className="fixed bottom-5 left-5 z-40 group flex items-center gap-2 px-4 py-2.5 rounded-full transition hover:scale-[1.03]"
          style={{
            background: "linear-gradient(180deg, rgba(24,24,24,0.95), rgba(11,11,11,0.98))",
            border: "1px solid rgba(212,175,55,0.4)",
            boxShadow: "0 12px 32px rgba(0,0,0,0.6), inset 0 1px 0 rgba(212,175,55,0.15)",
          }}
          aria-label="AI Guide"
          data-testid="ai-guide-btn">
          <div className="w-6 h-6 rounded-full flex items-center justify-center" style={{ background: "rgba(212,175,55,0.15)" }}>
            <Wand2 size={13} color="#D4AF37" />
          </div>
          <span className="text-xs font-semibold" style={{ color: "#F5F5F5" }}>
            {isFr ? "Guide IA" : "AI Guide"}
          </span>
        </button>
      )}
      {open && (
        <div
          className="fixed bottom-5 left-5 w-[340px] max-w-[calc(100vw-2.5rem)] z-40 rounded-2xl overflow-hidden"
          style={{
            background: "linear-gradient(180deg, rgba(24,24,24,0.98), rgba(11,11,11,0.98))",
            border: "1px solid rgba(212,175,55,0.35)",
            boxShadow: "0 20px 60px rgba(0,0,0,0.85)",
          }}
          data-testid="ai-guide-panel">
          <div className="flex items-center justify-between px-4 py-3 border-b" style={{ borderColor: "rgba(212,175,55,0.18)" }}>
            <div className="flex items-center gap-2">
              <Sparkles size={15} color="#D4AF37" />
              <span className="font-heading text-sm font-bold" style={{ color: "#F5F5F5" }}>
                {isFr ? "Guide PROGÈRE AI" : "PROGÈRE AI Guide"}
              </span>
            </div>
            <button onClick={() => setOpen(false)} className="p-1 rounded hover:bg-white/5" aria-label="Close" data-testid="ai-guide-close">
              <X size={15} color="rgba(245,245,245,0.7)" />
            </button>
          </div>
          <div className="p-4 space-y-3">
            <div className="text-[10px] uppercase tracking-widest font-semibold" style={{ color: "#D4AF37" }}>
              {isFr ? "Cette page" : "This page"}
            </div>
            <div className="font-heading text-base font-bold" style={{ color: "#F5F5F5" }}>{guide.title}</div>
            <p className="text-xs leading-relaxed" style={{ color: "rgba(245,245,245,0.75)" }}>{guide.body}</p>
            {guide.tips && guide.tips.length > 0 && (
              <ul className="space-y-1.5 mt-2">
                {guide.tips.map((tip, i) => (
                  <li key={i} className="flex items-start gap-2 text-xs" style={{ color: "rgba(245,245,245,0.7)" }}>
                    <span style={{ color: "#D4AF37" }}>•</span>{tip}
                  </li>
                ))}
              </ul>
            )}
            <button
              onClick={() => { setOpen(false); nav("/app/assistant"); }}
              className="btn-gold w-full mt-3 text-sm"
              data-testid="ai-guide-open-assistant">
              <Sparkles size={13} /> {isFr ? "Poser une question à l'IA" : "Ask the AI"} <ArrowRight size={13} />
            </button>
          </div>
        </div>
      )}
    </>
  );
}
